import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SatsangiDto, SatsangiDtoInit } from "../dto";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  InputSubmitWrapper,
  SubmitCurd,
  InputTextWrapper,
  InputRadioWrapper,
  InputSelectWrapper,
  InputDateNode,
  InputSwitch,
} from "../../../../common/globalCurd";
import { MessageContext } from "../../../../../_metronic/layout/core";
// import MapboxAddressAutocomplete from "../../../../common/mapboxAddressAutocomplete";
import GooglePlacesAutocomplete from "../../../../common/google-maps/googlePlacesAutocomplete";
import { RegistrationPreview } from "./public/registration-preview";
import { ThankYou } from "./public/registration-thankyou";

// Create the missing InputRadioWrapper component

const API_URL = process.env.REACT_APP_API_URL;
const apiPath = "satsangi";
const adminPath = "satsangi/admin";

// Helper function to format date as "DD MMM YYYY"
const formatDateLong = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Helper function to convert name to sentence case (first letter uppercase, rest lowercase)
const toSentenceCase = (name: string): string => {
  if (!name) return "";
  // Remove spaces and special characters except hyphens, keep only English letters and hyphens
  const cleaned = name.replace(/[^a-zA-Z-]/g, "");
  // Convert to sentence case
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

const CURD: React.FC = () => {
  const { messageContent, setmessageContent } = useContext(
    MessageContext as any
  ) as any;
  let { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [CatData, setUpdatedData] = useState([] as any[]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mandirOptions, setMandirOptions] = useState<any[]>([]);
  const [loadingMandirs, setLoadingMandirs] = useState(false);
  const [showCustomNativeVillage, setShowCustomNativeVillage] = useState(false);
  const [showCustomSevaDepartment, setShowCustomSevaDepartment] =
    useState(false);
  const [customSevaDepartment, setCustomSevaDepartment] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [consentConfig, setConsentConfig] = useState<any>({
    requiresGdprConsent: false,
    requiresBloodTypeConsent: false,
    requiresRestrictedTransferConsent: false,
    gdprConsentText: "",
    dataControllerName: "",
    dataControllerWebsite: "",
    privacyPolicyUrl: "",
    consentWithdrawalContact: "",
    gdprIntroText: "",
    gdprDataUsageText: "",
    gdprLegalBasisText: "",
    gdprDataSharingText: "",
    gdprDataStorageText: "",
    gdprSpecialCategoryTitle: "",
    gdprSpecialCategoryText: "",
    gdprSpecialCategoryConsentLabel: "",
    gdprRestrictedTransferTitle: "",
    gdprRestrictedTransferText: "",
    gdprRestrictedTransferConsentLabel: "",
    gdprWithdrawalText: "",
  });
  const [loadingConsentConfig, setLoadingConsentConfig] = useState(false);

  // Image crop states
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalPhotoData, setOriginalPhotoData] = useState<string | null>(
    null
  ); // Store original photo data URL
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropError, setCropError] = useState<string | null>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);
  const [cropSize, setCropSize] = useState<number>(360);

  // Better pinch tracking with refs to avoid stale closure issues
  const pinchStartDistance = useRef<number | null>(null);
  const pinchStartZoom = useRef<number>(1);
  const isPinching = useRef<boolean>(false);
  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  // Compute dynamic cropSize when modal opens and on resize so the circle matches the canvas
  useEffect(() => {
    if (!showCropModal) return;
    const compute = () => {
      const container = cropContainerRef.current;
      if (container) {
        const w = container.clientWidth;
        const h = container.clientHeight;
        // Use 90% of the smaller dimension for a larger, more usable crop circle
        const size = Math.floor(Math.min(w, h) * 0.9);
        setCropSize(size);
      }
    };
    compute();
    const onResize = () => compute();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [showCropModal, imageToCrop]);

  // Calculate initial zoom to fit image nicely when modal opens
  useEffect(() => {
    if (!showCropModal || !imageToCrop) return;

    const img = new Image();
    img.onload = () => {
      const container = cropContainerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      // Calculate what zoom would make the image fit the container exactly
      const widthRatio = containerWidth / imgWidth;
      const heightRatio = containerHeight / imgHeight;

      // Use the smaller ratio so the image fits within the container
      const fitZoom = Math.min(widthRatio, heightRatio);

      // Calculate the actual rendered size at this zoom
      const renderedWidth = imgWidth * fitZoom;
      const renderedHeight = imgHeight * fitZoom;

      // Calculate what the crop circle size will be (90% of smaller container dimension)
      const calculatedCropSize = Math.floor(
        Math.min(containerWidth, containerHeight) * 0.9
      );

      // Now adjust zoom so the rendered image matches or slightly exceeds the crop circle
      // This ensures the crop circle can be filled
      const cropWidthRatio = calculatedCropSize / imgWidth;
      const cropHeightRatio = calculatedCropSize / imgHeight;

      // Use the larger of the two to ensure crop circle coverage
      const initialZoom = Math.max(cropWidthRatio, cropHeightRatio);

      // Clamp between 0.01 and 3 (1% to 300%)
      const clampedZoom = Math.max(0.01, Math.min(3, initialZoom));

      setZoom(clampedZoom);
      setCrop({ x: 0, y: 0 }); // Center the image

      console.log("Initial zoom calculated:", {
        imgSize: { width: imgWidth, height: imgHeight },
        containerSize: { width: containerWidth, height: containerHeight },
        fitZoom,
        renderedSize: { width: renderedWidth, height: renderedHeight },
        calculatedCropSize,
        cropRatios: { width: cropWidthRatio, height: cropHeightRatio },
        calculatedZoom: initialZoom,
        appliedZoom: clampedZoom,
      });
    };

    img.src = imageToCrop;
  }, [showCropModal, imageToCrop]);

  // Real-time validation while user zooms/drags
  useEffect(() => {
    if (!showCropModal || !imageToCrop) return;

    // Validate crop position in real-time
    const validation = validateCropPosition();
    if (!validation.isValid) {
      setCropError(validation.message);
    } else {
      setCropError(null);
    }
  }, [crop, zoom, showCropModal, imageToCrop]);

  const sevaOptions = [
    { value: "Pujari", label: "Pujari" },
    { value: "Kothar", label: "Kothar" },
    { value: "Decoration", label: "Decoration" },
    { value: "Vaasan - Cleaning", label: "Vaasan - Cleaning" },
    { value: "Cleaning", label: "Cleaning" },
    { value: "Sound", label: "Sound" },
    { value: "Video", label: "Video" },
    { value: "Photo", label: "Photo" },
    { value: "IT - Web", label: "IT - Web" },
    { value: "IT - Networking", label: "IT - Networking" },
    { value: "IT - Graphics", label: "IT - Graphics" },
    { value: "Sahitya (Literature)", label: "Sahitya (Literature)" },
    { value: "Mahotsav", label: "Mahotsav" },
    { value: "Yatra", label: "Yatra" },
  ];

  const bloodGroupOptions = [
    { _id: "A+", name: "A+" },
    { _id: "A-", name: "A-" },
    { _id: "B+", name: "B+" },
    { _id: "B-", name: "B-" },
    { _id: "AB+", name: "AB+" },
    { _id: "AB-", name: "AB-" },
    { _id: "O+", name: "O+" },
    { _id: "O-", name: "O-" },
    { _id: "Not Sure", name: "Not Sure" },
  ];

  const genderOptions = [
    { _id: "Male", name: "Male" },
    { _id: "Female", name: "Female" },
  ];

  const maritalStatusOptions = [
    { _id: "Single", name: "Single" },
    { _id: "Married", name: "Married" },
    { _id: "Widowed", name: "Widowed" },
  ];

  const nativeVillageOptions = [
    { _id: "Ahmedabad", name: "Ahmedabad" },
    { _id: "Akhaj", name: "Akhaj" },
    { _id: "Anand", name: "Anand" },
    { _id: "Bavla", name: "Bavla" },
    { _id: "Bharasar", name: "Bharasar" },
    { _id: "Bhuj", name: "Bhuj" },
    { _id: "Dharampur", name: "Dharampur" },
    { _id: "Ghanshyamnagar", name: "Ghanshyamnagar" },
    { _id: "Godpar", name: "Godpar" },
    { _id: "Gavada", name: "Gavada" },
    { _id: "Kalol", name: "Kalol" },
    { _id: "Kanbha", name: "Kanbha" },
    { _id: "Karjisan", name: "Karjisan" },
    { _id: "Kera", name: "Kera" },
    { _id: "Kadi", name: "Kadi" },
    { _id: "Kundalpur", name: "Kundalpur" },
    { _id: "Langhnaj", name: "Langhnaj" },
    { _id: "Madhapur", name: "Madhapur" },
    { _id: "Maninagar", name: "Maninagar" },
    { _id: "Mandvi", name: "Mandvi" },
    { _id: "Mankuva", name: "Mankuva" },
    { _id: "Mokhasan", name: "Mokhasan" },
    { _id: "Mota Gokharwara", name: "Mota Gokharwara" },
    { _id: "Naranpar", name: "Naranpar" },
    { _id: "Rampar", name: "Rampar" },
    { _id: "Ranip", name: "Ranip" },
    { _id: "Sanjaya", name: "Sanjaya" },
    { _id: "Soja", name: "Soja" },
    { _id: "Sukhpur", name: "Sukhpur" },
    { _id: "Surat", name: "Surat" },
    { _id: "Trent", name: "Trent" },
    { _id: "Vadnagar", name: "Vadnagar" },
    { _id: "Vadodara", name: "Vadodara" },
    { _id: "Viramgam", name: "Viramgam" },
    { _id: "Vrushpur", name: "Vrushpur" },
    { _id: "Other", name: "Other" },
  ];

  const localMandirOptions = [
    { _id: "London, UK", name: "London, UK" },
    { _id: "Bolton, UK", name: "Bolton, UK" },
    { _id: "Nairobi, Kenya", name: "Nairobi, Kenya" },
    { _id: "New Jersey, USA", name: "New Jersey, USA" },
    { _id: "Chicago, USA", name: "Chicago, USA" },
    { _id: "Tennesee, USA", name: "Tennesee, USA" },
    { _id: "Perth, Australia", name: "Perth, Australia" },
    { _id: "Ghanshyamnagar, India", name: "Ghanshyamnagar, India" },
    { _id: "Bharasar, India", name: "Bharasar, India" },
    { _id: "Kera, India", name: "Kera, India" },
    { _id: "Kundalpur, India", name: "Kundalpur, India" },
    { _id: "Naranpar, India", name: "Naranpar, India" },
    { _id: "Vrushpur, India", name: "Vrushpur, India" },
    { _id: "Madhapur, India", name: "Madhapur, India" },
    { _id: "Maninagar, India", name: "Maninagar, India" },
    { _id: "Mandvi, India", name: "Mandvi, India" },
    { _id: "Mankuva, India", name: "Mankuva, India" },
    { _id: "Mota Gokharwara, India", name: "Mota Gokharwara, India" },
    { _id: "Rampar, India", name: "Rampar, India" },
    { _id: "Sanjaya, India", name: "Sanjaya, India" },
    { _id: "Sukhpur, India", name: "Sukhpur, India" },
    { _id: "Godpar, India", name: "Godpar, India" },
    { _id: "Other", name: "Other" },
  ];

  const countryCodeOptions = [
    { _id: "US_CA", value: "+1", name: "USA / Canada", flag: "🇺🇸/🇨🇦" },
    { _id: "GB", value: "+44", name: "UK", flag: "🇬🇧" },
    { _id: "AU", value: "+61", name: "Australia", flag: "🇦🇺" },
    { _id: "NZ", value: "+64", name: "New Zealand", flag: "🇳🇿" },
    { _id: "IN", value: "+91", name: "India", flag: "🇮🇳" },
    { _id: "SC", value: "+248", name: "Seychelles", flag: "🇸🇨" },
    { _id: "SC", value: "+250", name: "Rwanda", flag: "🇷🇼" },
    { _id: "KE", value: "+254", name: "Kenya", flag: "🇰🇪" },
    { _id: "TZ", value: "+255", name: "Tanzania", flag: "🇹🇿" },
    { _id: "UG", value: "+256", name: "Uganda", flag: "🇺🇬" },
  ];

  // Add country options for address
  const countryOptions = [
    { _id: "Australia", name: "Australia" },
    { _id: "Canada", name: "Canada" },
    { _id: "India", name: "India" },
    { _id: "Kenya", name: "Kenya" },
    { _id: "Tanzania", name: "Tanzania" },
    { _id: "Uganda", name: "Uganda" },
    { _id: "United Kingdom", name: "United Kingdom" },
    { _id: "United States", name: "United States" },
    { _id: "New Zealand", name: "New Zealand" },
  ];

  const getValidationSchema = (consentConfig: any) =>
    Yup.object().shape({
      id: Yup.string().default(id),
      firstName: Yup.string()
        .required("Given Name is required")
        .matches(/^[a-zA-Z-]+$/, "Only English letters and hyphens are allowed")
        .test(
          "is-sentence-case",
          "Name must be in sentence case (e.g. Krina)",
          function (value) {
            if (!value) return true;
            const expected = toSentenceCase(value);
            return value === expected;
          }
        ),
      middleName: Yup.string()
        .required("Father/Husband Name is required")
        .matches(/^[a-zA-Z-]+$/, "Only English letters and hyphens are allowed")
        .test(
          "is-sentence-case",
          "Name must be in sentence case (e.g. Jayesh)",
          function (value) {
            if (!value) return true;
            const expected = toSentenceCase(value);
            return value === expected;
          }
        ),
      lastName: Yup.string()
        .required("Surname is required")
        .matches(/^[a-zA-Z-]+$/, "Only English letters and hyphens are allowed")
        .test(
          "is-sentence-case",
          "Name must be in sentence case (e.g. Varsani)",
          function (value) {
            if (!value) return true;
            const expected = toSentenceCase(value);
            return value === expected;
          }
        ),
      dateOfBirth: Yup.string()
        .required("Date of Birth is required")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
        .test(
          "is-past-date",
          "Date of Birth must be in the past",
          function (value) {
            if (!value) return false;
            const today = new Date().toISOString().split("T")[0];
            return value < today;
          }
        ),
      localMandir: Yup.string().required("Local Mandir is required"),
      nativeVillage: Yup.string().required("Native Village is required"),
      customNativeVillage: Yup.string().when("nativeVillage", {
        is: "Other",
        then: Yup.string()
          .required("Please enter your native village")
          .min(2, "Native village must be at least 2 characters"),
        otherwise: Yup.string().notRequired(),
      }),
      bloodGroup: Yup.string().required("Blood Group is required"),
      mobileCountryCode: Yup.string().required("Country code is required"),
      mobileNumber: Yup.string()
        .required("Mobile Number is required")
        .min(5, "Mobile number must be at least 5 digits")
        .matches(/^\d+$/, "Mobile number must contain only digits"),
      // Add address fields validation
      street: Yup.string().required("House No. & Street Name is required"),
      area: Yup.string().when("country", {
        is: "India",
        then: Yup.string().required("Area is required"),
        otherwise: Yup.string(),
      }),
      city: Yup.string().required("City is required"),
      state: Yup.string().optional(),
      postcode: Yup.string().required("Postal/PIN/ZIP Code is required"),
      country: Yup.string().required("Country is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      gender: Yup.string().required("Gender is required"),
      maritalStatus: Yup.string().required("Marital Status is required"),
      education: Yup.string().required("Education is required"),
      profession: Yup.string().required("Profession is required"),
      isRetired: Yup.boolean(),
      wouldLikeToVolunteer: Yup.boolean(),
      sevaDepartments: Yup.array().when("wouldLikeToVolunteer", {
        is: true,
        then: Yup.array().min(1, "Select at least 1 Seva department"),
        otherwise: Yup.array(),
      }),
      photo: Yup.mixed()
        .required("Photo is required")
        .test("fileSize", "File size is too large (max 15MB)", (value) => {
          // Only validate if a file is selected (File object)
          if (value && value instanceof File) {
            return value.size <= 16 * 1024 * 1024; // 16MB in bytes
          }
          // If editing and value is a string (existing image), skip size check
          return true;
        })
        .test("fileType", "Only JPG and PNG images are allowed", (value) => {
          // Only validate if a file is selected (File object)
          if (value && value instanceof File) {
            const validTypes = ["image/jpeg", "image/jpg", "image/png"];
            return validTypes.includes(value.type);
          }
          // If editing and value is a string (existing image), skip type check
          return true;
        }),
      // acceptDisclaimer only required when GDPR consent is NOT required
      acceptDisclaimer: !consentConfig.requiresGdprConsent
        ? Yup.boolean().oneOf([true], "You must accept the privacy notice")
        : Yup.boolean(),
      // Explicit consent fields - dynamic validation based on consentConfig
      consentBloodType: consentConfig.requiresBloodTypeConsent
        ? Yup.boolean().oneOf(
            [true],
            "You must provide consent for blood type processing"
          )
        : Yup.boolean(),
      consentBloodTypeName: Yup.string(),
      consentBloodTypeDate: Yup.string(),
      consentRestrictedTransfer: consentConfig.requiresRestrictedTransferConsent
        ? Yup.boolean().oneOf(
            [true],
            "You must provide consent for restricted transfer to India"
          )
        : Yup.boolean(),
      consentRestrictedTransferName: Yup.string(),
      consentRestrictedTransferDate: Yup.string(),
    });

  useEffect(() => {
    if (id !== undefined) {
      setLoading(true);
      axios
        .get(`${API_URL}${apiPath}/${id}`)
        .then((json) => {
          console.log("Fetched data:", json.data.data);
          const apiData = json.data.data[0];
          const {
            _id,
            __v,
            createdBy,
            createdAt,
            updatedAt,
            updatedBy,
            idCardNumber,
            lastScanned,
            scanCount,
            idCardPath,
            reviewStatus,
            ...rest
          } = apiData;

          setShowCustomNativeVillage(apiData.nativeVillage === "Other");

          const initialFormValues = {
            ...rest,
            street: rest.street || "",
            customNativeVillage: rest.customNativeVillage || "",
          };

          setUpdatedData([initialFormValues]);

          // S3/CDN domain for constructing full URLs
          const s3Domain =
            process.env.REACT_APP_S3_DOMAIN ||
            "https://sgadiast.swaminarayangadi.com";

          // Load photo with full URL
          if (rest.photo) {
            let photoUrl = null;
            if (rest.photo.startsWith("http")) {
              // Already a full URL
              photoUrl = rest.photo;
            } else if (rest.photo.startsWith("/")) {
              // Absolute path starting with /
              photoUrl = `${s3Domain}${rest.photo}`;
            } else {
              // Relative path, add /satsangi/ prefix
              photoUrl = `${s3Domain}/satsangi/${rest.photo}`;
            }

            setImagePreview(photoUrl);
            console.log("Loaded photo from API:", photoUrl);
          }

          // Load original photo with full URL
          if (rest.originalPhoto) {
            let originalPhotoUrl = null;
            if (rest.originalPhoto.startsWith("http")) {
              // Already a full URL
              originalPhotoUrl = rest.originalPhoto;
            } else if (rest.originalPhoto.startsWith("/")) {
              // Absolute path starting with /
              originalPhotoUrl = `${s3Domain}${rest.originalPhoto}`;
            } else {
              // Relative path, add /satsangi/ prefix
              originalPhotoUrl = `${s3Domain}/satsangi/${rest.originalPhoto}`;
            }

            console.log("Loaded originalPhoto from API:", originalPhotoUrl);
            setOriginalPhotoData(originalPhotoUrl);
          } else if (rest.photo) {
            // For backwards compatibility, use the cropped photo as original if no original exists
            let photoUrl = null;
            if (rest.photo.startsWith("http")) {
              photoUrl = rest.photo;
            } else if (rest.photo.startsWith("/")) {
              photoUrl = `${s3Domain}${rest.photo}`;
            } else {
              photoUrl = `${s3Domain}/satsangi/${rest.photo}`;
            }

            console.log(
              "No originalPhoto in API response, using photo as fallback:",
              photoUrl
            );
            setOriginalPhotoData(photoUrl);
          } else {
            console.log("No photo or originalPhoto found in API response");
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setLoading(false);
        });
    } else {
      setLoading(false);
      setUpdatedData([SatsangiDtoInit]);
    }
    fetchMandirOptions();
  }, [id]);

  // Add this function to fetch mandir options
  const fetchMandirOptions = async () => {
    setLoadingMandirs(true);
    try {
      const response = await axios.get(`${API_URL}mandir/public/all`);
      if (response.data && response.data.data) {
        // Transform the API data to match your expected format
        const transformedMandirs = response.data.data.map((mandir: any) => ({
          _id: mandir._id,
          title: `${mandir.title}, ${mandir.country}`,
        }));

        setMandirOptions(transformedMandirs);
      }
    } catch (error) {
      console.error("Error fetching mandir options:", error);
      // Fallback to local options if API fails
      setMandirOptions([...localMandirOptions]);
    } finally {
      setLoadingMandirs(false);
    }
  };

  const handleDateChange = (value: string) => {
    // Store date as YYYY-MM-DD string to avoid timezone issues
    formik.setFieldValue("dateOfBirth", value || "");
  };

  const handleLocalMandirChange = (value: string) => {
    formik.setFieldValue("localMandir", value);
  };

  const handleNativeVillageChange = (value: string) => {
    formik.setFieldValue("nativeVillage", value);
    setShowCustomNativeVillage(value === "Other");
    if (value !== "Other") {
      formik.setFieldValue("customNativeVillage", "");
    }
  };

  const handleCustomNativeVillageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.setFieldValue("customNativeVillage", e.target.value);
  };

  const handleRetiredChange = () => {
    formik.setFieldValue("isRetired", !formik.values.isRetired);
  };

  const handlewouldLikeToVolunteerChange = () => {
    const newValue = !formik.getFieldProps("wouldLikeToVolunteer").value;
    formik.setFieldValue("wouldLikeToVolunteer", newValue);

    // Clear seva preferences if not willing to serve
    if (!newValue) {
      formik.setFieldValue("sevaDepartments", []);
    }
  };

  const handleCustomSevaDepartmentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomSevaDepartment(e.target.value);
  };

  const addCustomSevaDepartment = () => {
    if (customSevaDepartment.trim()) {
      const currentValues = formik.values.sevaDepartments || [];
      if (!currentValues.includes(customSevaDepartment)) {
        formik.setFieldValue("sevaDepartments", [
          ...currentValues,
          customSevaDepartment,
        ]);
        setCustomSevaDepartment("");
        setShowCustomSevaDepartment(false);
      }
    }
  };

  const handleBloodGroupChange = (value: string) => {
    formik.setFieldValue("bloodGroup", value);
  };

  const handleMaritalStatus = (value: string) => {
    formik.setFieldValue("maritalStatus", value);
  };

  const handleGender = (value: string) => {
    formik.setFieldValue("gender", value);
  };

  const handleCountryCodeChange = (value: string) => {
    formik.setFieldValue("mobileCountryCode", value);
  };

  const handleCountryChange = (value: string) => {
    formik.setFieldValue("country", value);
  };

  const handleAddressSelect = (address: any) => {
    formik.setFieldValue("street", address.street);
    formik.setFieldValue("city", address.city);
    formik.setFieldValue("state", address.state);
    formik.setFieldValue("postcode", address.postcode);
    formik.setFieldValue("country", address.country);
    setStreetAddress(address.full_address || "");
  };

  // Create a cropped image from canvas
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  // Validate that the image is properly positioned within the crop guideline
  const validateCropPosition = (): { isValid: boolean; message: string } => {
    const renderedImage = cropImageRef.current;
    if (!renderedImage) {
      return { isValid: false, message: "Image not loaded" };
    }

    const containerWidth = renderedImage.parentElement?.clientWidth || 800;
    const containerHeight = 400;
    // cropSize state will be used below

    // Calculate center of container
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // With scale transform, calculate the scaled dimensions
    const scaledWidth = renderedImage.naturalWidth * zoom;
    const scaledHeight = renderedImage.naturalHeight * zoom;

    // Calculate where the image starts (top-left corner)
    const imageStartX = centerX - scaledWidth / 2 + crop.x;
    const imageStartY = centerY - scaledHeight / 2 + crop.y;

    // Calculate image end position (bottom-right corner)
    const imageEndX = imageStartX + scaledWidth;
    const imageEndY = imageStartY + scaledHeight;

    // Calculate crop area boundaries
    const cropAreaLeft = centerX - cropSize / 2;
    const cropAreaRight = centerX + cropSize / 2;
    const cropAreaTop = centerY - cropSize / 2;
    const cropAreaBottom = centerY + cropSize / 2;

    // Check if the crop area is fully within the image bounds
    // The image must cover the entire circular guideline area
    const isFullyCovered =
      imageStartX <= cropAreaLeft &&
      imageEndX >= cropAreaRight &&
      imageStartY <= cropAreaTop &&
      imageEndY >= cropAreaBottom;

    if (!isFullyCovered) {
      return {
        isValid: false,
        message:
          "Please position your photo so it completely fills the circular guideline. Zoom in or adjust the position to ensure your entire face is visible within the circle.",
      };
    }

    return { isValid: true, message: "" };
  };

  const getCroppedImg = async (imageSrc: string): Promise<Blob | null> => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return null;
      }

      // Set canvas size to a square for the circular crop
      // use dynamic cropSize state
      const _cropSize = cropSize;
      canvas.width = _cropSize;
      canvas.height = _cropSize;

      // Get the actual rendered image from the crop modal
      const renderedImage = cropImageRef.current;
      if (!renderedImage) {
        console.error("No rendered image reference");
        return null;
      }

      // With scale() transform, the natural size is maintained but scaled
      // displayWidth/Height reflect the scaled size
      const displayWidth = renderedImage.naturalWidth * zoom;
      const displayHeight = renderedImage.naturalHeight * zoom;

      console.log("Image dimensions:", {
        natural: { width: image.width, height: image.height },
        displayed: { width: displayWidth, height: displayHeight },
        zoom,
        crop,
      });

      // Container dimensions
      const containerWidth = renderedImage.parentElement?.clientWidth || 800;
      const containerHeight = 400;

      // Calculate the center of the container
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;

      // Calculate where the image starts (top-left corner) in the container
      const imageStartX = centerX - displayWidth / 2 + crop.x;
      const imageStartY = centerY - displayHeight / 2 + crop.y;

      // Calculate the crop area in the container (center of container is where the circle is)
      const cropAreaX = centerX - _cropSize / 2;
      const cropAreaY = centerY - _cropSize / 2;

      // Convert container coordinates to source image coordinates
      const scaleX = image.width / displayWidth;
      const scaleY = image.height / displayHeight;

      const sourceX = (cropAreaX - imageStartX) * scaleX;
      const sourceY = (cropAreaY - imageStartY) * scaleY;
      const sourceSize = _cropSize * scaleX;

      console.log("Crop calculation:", {
        container: { centerX, centerY },
        imageStart: { x: imageStartX, y: imageStartY },
        cropArea: { x: cropAreaX, y: cropAreaY },
        source: { x: sourceX, y: sourceY, size: sourceSize },
        scale: { x: scaleX, y: scaleY },
      });

      // Draw the cropped portion first
      ctx.drawImage(
        image,
        Math.max(0, sourceX),
        Math.max(0, sourceY),
        Math.min(sourceSize, image.width),
        Math.min(sourceSize, image.height),
        0,
        0,
        _cropSize,
        _cropSize
      );

      // Create circular mask
      ctx.globalCompositeOperation = "destination-in";
      ctx.beginPath();
      ctx.arc(_cropSize / 2, _cropSize / 2, _cropSize / 2, 0, Math.PI * 2);
      ctx.fill();

      // Return as blob with PNG format to maintain transparency
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            console.log("Blob created successfully, size:", blob.size);
          } else {
            console.error("Failed to create blob");
          }
          resolve(blob);
        }, "image/png");
      });
    } catch (error) {
      console.error("Error in getCroppedImg:", error);
      return null;
    }
  };

  const handleCropSave = async () => {
    try {
      if (!imageToCrop) {
        console.error("No image to crop");
        return;
      }

      // Validate crop position before proceeding
      const validation = validateCropPosition();
      if (!validation.isValid) {
        setCropError(validation.message);
        return;
      }

      // Clear any previous errors
      setCropError(null);

      console.log("Starting crop with:", { crop, zoom });
      const croppedImageBlob = await getCroppedImg(imageToCrop);

      if (croppedImageBlob) {
        console.log(
          "Cropped image blob created:",
          croppedImageBlob.size,
          "bytes"
        );

        // Convert blob to file
        const croppedFile = new File([croppedImageBlob], "cropped-photo.png", {
          type: "image/png",
        });

        console.log("Cropped file created:", croppedFile);

        // Set the cropped file to formik
        formik.setFieldValue("photo", croppedFile);

        // Also save the original photo to formik
        if (originalPhotoData) {
          // Convert original photo data URL to blob and then to file
          const originalBlob = await fetch(originalPhotoData).then((r) =>
            r.blob()
          );
          const originalFile = new File([originalBlob], "original-photo.png", {
            type: "image/png",
          });
          formik.setFieldValue("originalPhoto", originalFile);
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          console.log("Setting image preview, length:", result?.length);
          setImagePreview(result);
        };
        reader.onerror = (e) => {
          console.error("FileReader error:", e);
        };
        reader.readAsDataURL(croppedFile);

        // Close modal and reset states
        setShowCropModal(false);
        setImageToCrop(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropError(null);

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        console.error("Failed to create cropped image blob");
        setmessageContent({
          ...messageContent,
          show: true,
          type: "error",
          message: "Failed to crop image. Please try again.",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      setmessageContent({
        ...messageContent,
        show: true,
        type: "error",
        message: "Failed to crop image. Please try again.",
        duration: 5000,
      });
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        setmessageContent({
          ...messageContent,
          show: true,
          type: "error",
          message:
            "Only JPG and PNG images are allowed. Please select a different file.",
          duration: 5000,
        });
        // Clear the file input
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      // Validate file size (15MB)
      if (file.size > 16 * 1024 * 1024) {
        setmessageContent({
          ...messageContent,
          show: true,
          type: "error",
          message: "File size is too large. Maximum allowed size is 15MB.",
          duration: 5000,
        });
        // Clear the file input
        if (event.target) {
          event.target.value = "";
        }
        return;
      }

      // Open crop modal with the image
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target?.result as string;
        setImageToCrop(imageDataUrl);
        setOriginalPhotoData(imageDataUrl); // Store the original photo data
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const openCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute("capture", "camera");
      fileInputRef.current.click();
    }
  };

  const openGallery = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute("capture");
      fileInputRef.current.click();
    }
  };

  const handleReCrop = () => {
    // Open crop modal with the original photo if it exists
    console.log("handleReCrop called - originalPhotoData:", originalPhotoData);
    console.log("handleReCrop called - imagePreview:", imagePreview);

    if (originalPhotoData) {
      console.log("Using originalPhotoData for re-crop:", originalPhotoData);
      setImageToCrop(originalPhotoData);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropError(null);
    } else if (imagePreview) {
      // Fallback to current preview if no original exists (for old records)
      console.log(
        "No originalPhotoData found, using imagePreview:",
        imagePreview
      );
      setImageToCrop(imagePreview);
      setOriginalPhotoData(imagePreview); // Save it as original for future re-crops
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropError(null);
    } else {
      console.error("No image available for re-cropping");
    }
  };

  const renderData = () => {
    if (id !== undefined && CatData.length > 0) {
      // Normalize dateOfBirth to YYYY-MM-DD format if needed
      if (
        CatData[0].dateOfBirth &&
        typeof CatData[0].dateOfBirth === "string"
      ) {
        // Check if it's in ISO format (YYYY-MM-DD) - keep as is
        if (
          CatData[0].dateOfBirth.includes("-") &&
          CatData[0].dateOfBirth.length === 10
        ) {
          // Already in correct format, no conversion needed
        } else if (CatData[0].dateOfBirth.includes("/")) {
          // Convert DD/MM/YYYY to YYYY-MM-DD format
          const dateParts = CatData[0].dateOfBirth.split("/");
          if (dateParts.length === 3) {
            const day = dateParts[0].padStart(2, "0");
            const month = dateParts[1].padStart(2, "0");
            const year = dateParts[2];
            CatData[0].dateOfBirth = `${year}-${month}-${day}`;
          }
        }
      }

      // Convert mobileCountryCode from numeric to +prefixed format
      if (
        CatData[0].mobileCountryCode &&
        typeof CatData[0].mobileCountryCode === "string"
      ) {
        // If it's just a number without +, add the + prefix
        if (!CatData[0].mobileCountryCode.startsWith("+")) {
          CatData[0].mobileCountryCode = "+" + CatData[0].mobileCountryCode;
        }
      }

      return CatData[0];
    }
    return SatsangiDtoInit;
  };

  const formik = useFormik<SatsangiDto>({
    initialValues: renderData(),
    enableReinitialize: true,
    validationSchema: getValidationSchema(consentConfig),
    validateOnChange: true,
    onSubmit: async (values) => {
      setLoading(true);

      // Create FormData to handle file upload
      const formData = new FormData();

      // Convert boolean values to strings that can be properly parsed
      const processedValues = {
        ...values,
        isRetired: values.isRetired ? "true" : "false",
        wouldLikeToVolunteer: values.wouldLikeToVolunteer ? "true" : "false",
        acceptDisclaimer: values.acceptDisclaimer ? "true" : "false",
      };

      Object.keys(processedValues).forEach((key) => {
        const value = processedValues[key as keyof typeof processedValues];

        if (key === "sevaDepartments" && Array.isArray(value)) {
          // Handle array data - append each item separately
          if (values.wouldLikeToVolunteer) {
            if (value.length === 0) {
              formData.append("sevaDepartments", "");
            } else {
              value.forEach((pref, index) => {
                formData.append(`sevaDepartments[${index}]`, pref);
              });
            }
          }
        } else if (key === "originalPhoto" && value instanceof File) {
          // Handle original photo upload
          formData.append("originalPhoto", value);
        } else if (key === "photo" && value instanceof File) {
          // Handle cropped photo upload
          formData.append("photo", value);
        } else if (key === "dateOfBirth" && typeof value === "string") {
          // Send date in ISO 8601 format (YYYY-MM-DD) as expected by API
          if (value && value.includes("-")) {
            // Already in YYYY-MM-DD format (ISO 8601)
            formData.append(key, value);
          } else if (value && value.includes("/")) {
            // Convert from DD/MM/YYYY to YYYY-MM-DD
            const [day, month, year] = value.split("/");
            formData.append(key, `${year}-${month}-${day}`);
          } else {
            formData.append(key, value);
          }
        } else if (key === "mobileCountryCode" && typeof value === "string") {
          // Remove + prefix for API submission
          const numericCode = value.startsWith("+")
            ? value.substring(1)
            : value;
          formData.append(key, numericCode);
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      try {
        const Res = await SubmitCurd(
          navigate,
          formData,
          API_URL,
          apiPath + "/",
          adminPath,
          id
        );

        if (Res.isError !== undefined && Res.isError !== true) {
          setLoading(false);
          setmessageContent({
            ...messageContent,
            show: true,
            type: "success",
            message: Res.message,
            duration: 15000,
          });

          // For new registrations show thank you page, for updates redirect based on state or to admin list
          if (id) {
            // Check if we should return to a specific page (e.g., from review list)
            const returnTo = (location.state as any)?.returnTo;
            if (returnTo) {
              navigate(returnTo);
            } else {
              navigate(`/${adminPath}`);
            }
          } else {
            setShowThankYou(true);
            setShowPreview(false);
          }
        } else {
          setLoading(false);
          setmessageContent({
            ...messageContent,
            show: true,
            type: "error",
            message:
              Res.message !== undefined ? Res.message : "Something went wrong!",
            duration: 15000,
          });
        }
      } catch (error) {
        setLoading(false);
        setmessageContent({
          ...messageContent,
          show: true,
          type: "error",
          message: "Network error occurred. Please try again.",
          duration: 15000,
        });
      }
    },
  });

  // Fetch consent configuration when local mandir changes
  useEffect(() => {
    const fetchConsentConfig = async () => {
      // Only fetch if we have a mandir ID selected
      if (!formik.values.localMandir) {
        // Reset to defaults if no mandir selected
        setConsentConfig({
          requiresGdprConsent: false,
          requiresBloodTypeConsent: false,
          requiresRestrictedTransferConsent: false,
          gdprConsentText: "",
          dataControllerName: "",
          dataControllerWebsite: "",
          privacyPolicyUrl: "",
          consentWithdrawalContact: "",
        });
        return;
      }

      // Find the selected mandir to get its ID
      const selectedMandir = mandirOptions.find(
        (m) =>
          m._id === formik.values.localMandir ||
          m.title === formik.values.localMandir
      );

      if (!selectedMandir) {
        console.log("Selected mandir not found in options yet");
        return;
      }

      setLoadingConsentConfig(true);
      try {
        const response = await axios.get(
          `${API_URL}mandir/public/consent-config/${selectedMandir._id}`
        );
        if (response.data && response.data.data) {
          console.log("Consent config fetched:", response.data.data);
          setConsentConfig(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching consent config:", error);
        // Keep defaults on error
      } finally {
        setLoadingConsentConfig(false);
      }
    };

    fetchConsentConfig();
  }, [formik.values.localMandir, mandirOptions]);

  // Update consent name fields in real-time when user's name changes
  useEffect(() => {
    const fullName =
      `${formik.values.firstName} ${formik.values.middleName} ${formik.values.lastName}`.trim();

    // Only update if consent is checked and name has changed
    if (
      formik.values.consentBloodType &&
      formik.values.consentBloodTypeName !== fullName
    ) {
      formik.setFieldValue("consentBloodTypeName", fullName);
    }

    if (
      formik.values.consentRestrictedTransfer &&
      formik.values.consentRestrictedTransferName !== fullName
    ) {
      formik.setFieldValue("consentRestrictedTransferName", fullName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.firstName,
    formik.values.middleName,
    formik.values.lastName,
    formik.values.consentBloodType,
    formik.values.consentRestrictedTransfer,
  ]);

  const handlePreview = async () => {
    // Validate form first
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({
        firstName: true,
        middleName: true,
        lastName: true,
        dateOfBirth: true,
        localMandir: true,
        nativeVillage: true,
        customNativeVillage: formik.values.nativeVillage === "Other",
        bloodGroup: true,
        mobileCountryCode: true,
        mobileNumber: true,
        street: true,
        area: true,
        city: true,
        state: true,
        postcode: true,
        country: true,
        email: true,
        gender: true,
        maritalStatus: true,
        education: true,
        profession: true,
        sevaDepartments: formik.values.wouldLikeToVolunteer,
        photo: true,
        acceptDisclaimer: true,
        consentBloodType: true,
        consentBloodTypeName: true,
        consentBloodTypeDate: true,
        consentRestrictedTransfer: true,
        consentRestrictedTransferName: true,
        consentRestrictedTransferDate: true,
      });
      return;
    }

    // Set preview data and show preview
    setPreviewData(formik.values);
    setShowPreview(true);
  };

  const handleFinalSubmit = async () => {
    if (previewData) {
      // Use the original formik submit logic
      await formik.submitForm();
    }
  };

  const handleStartNewRegistration = () => {
    // Reset all form states
    setShowThankYou(false);
    setShowPreview(false);
    setPreviewData(null);
    formik.resetForm();
    setImagePreview(null);
    setShowCustomNativeVillage(false);
    setShowCustomSevaDepartment(false);
    setCustomSevaDepartment("");

    // Scroll to top for new registration
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {showThankYou ? (
        <ThankYou data={previewData} onStartNew={handleStartNewRegistration} />
      ) : showPreview ? (
        <RegistrationPreview
          data={previewData}
          imagePreview={imagePreview}
          countryCodeOptions={countryCodeOptions}
          mandirOptions={mandirOptions}
          loading={loading}
          onEdit={() => setShowPreview(false)}
          onSubmit={handleFinalSubmit}
        />
      ) : (
        <div className="card mb-5 mb-xl-10">
          {/* <CurdHeader
            adminPath={adminPath}
            id={id}
          /> */}

          <div className="card-header border-0 p-5 d-flex flex-column flex-sm-row align-items-center">
            {/* Logo */}
            <div className="me-sm-4 mb-3 mb-sm-0" style={{ flexShrink: 0 }}>
              <img
                src="/media/logos/logo-2.svg" // or import Logo from '../assets/logo.svg'
                alt="Swaminarayan Gadi Logo"
                style={{ width: 60, height: 60, objectFit: "contain" }}
              />
            </div>

            {/* Title */}
            <div className="flex-grow-1 text-center text-sm-start">
              <h3 className="card-title fw-bolder fs-5 fs-sm-3 mb-1">
                Maninagar Shree Swaminarayan Gadi Sansthan
              </h3>
              <span className="card-label fs-6 fs-sm-4">
                Shree Swaminarayan Gadi Parivar Registration Form
              </span>
            </div>
          </div>

          <div id="kt_account_profile_details" className="collapse show">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handlePreview();
              }}
              noValidate
              className="form"
            >
              <div className="card-body border-top p-9">
                {/* Data Privacy Notice - Shown for all mandirs */}
                <p>
                  Use this form to register for Shree Swaminarayan Gadi Parivar.
                  Please read the privacy notice and click on the tick boxes if
                  you agree. After the form is submitted and the information is
                  verified, you will receive a digital ID on your WhatsApp -
                  this process may take a few days. Please ensure your WhatsApp
                  mobile number is correctly entered in the form.
                </p>
                <p className="mb-2">
                  Maninagar Shree Swaminarayan Gadi Sansthan is the data
                  controller for this activity. You can find out more about the
                  Sansthan on our website:{" "}
                  <a
                    href={
                      consentConfig.dataControllerWebsite ||
                      "https://www.swaminarayangadi.com"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-semibold"
                  >
                    {consentConfig.dataControllerWebsite ||
                      "https://www.swaminarayangadi.com"}
                  </a>
                </p>
                <p className="mb-0">
                  If you choose to share your information, it may be used for
                  purposes such as communicating with you, improving security
                  and access control, and managing events (for example, entry to
                  eating areas during events). Your photograph will be used for
                  a digital identification which will be shared for the above
                  purposes.
                </p>

                {/* Contact Information Section */}
                <div className="section-header mb-6 mt-8">
                  <h3 className="section-title">
                    <i className="fas fa-address-card me-2"></i>Contact
                    Information
                  </h3>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-4 col-md-4 col-sm-12">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={{
                        ...formik.getFieldProps("firstName"),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const formatted = toSentenceCase(e.target.value);
                          formik.setFieldValue("firstName", formatted);
                        },
                      }}
                      title="Given or First Name"
                      placeholder="e.g. Jashu"
                      touchedName={formik.touched.firstName}
                      errorsName={formik.errors.firstName}
                    />
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-12">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={{
                        ...formik.getFieldProps("middleName"),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const formatted = toSentenceCase(e.target.value);
                          formik.setFieldValue("middleName", formatted);
                        },
                      }}
                      title="Father or Husband or Middle Name"
                      placeholder="e.g. Jigneshbhai"
                      touchedName={formik.touched.middleName}
                      errorsName={formik.errors.middleName}
                    />
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-12">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={{
                        ...formik.getFieldProps("lastName"),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const formatted = toSentenceCase(e.target.value);
                          formik.setFieldValue("lastName", formatted);
                        },
                      }}
                      title="Surname or Last Name"
                      placeholder="e.g. Patel"
                      touchedName={formik.touched.lastName}
                      errorsName={formik.errors.lastName}
                    />
                  </div>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="col-form-label required fw-bold fs-6">
                        Mobile Number (Whatsapp)
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-shrink-1">
                          <select
                            className={`form-select form-select-solid form-select-lg ${
                              formik.values.mobileCountryCode === ""
                                ? "text-muted"
                                : ""
                            }`}
                            value={formik.values.mobileCountryCode}
                            onChange={(e) =>
                              handleCountryCodeChange(e.target.value)
                            }
                            style={{ minWidth: "120px" }}
                          >
                            <option
                              selected={formik.values.mobileCountryCode === ""}
                              value=""
                              disabled
                            >
                              Select Code
                            </option>
                            {countryCodeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                ({option.flag}) {option.value}
                              </option>
                            ))}
                          </select>
                          {formik.touched.mobileCountryCode &&
                            formik.errors.mobileCountryCode && (
                              <div className="fv-plugins-message-container">
                                <div className="fv-help-block text-danger">
                                  {formik.errors.mobileCountryCode}
                                </div>
                              </div>
                            )}
                        </div>
                        <div className="flex-grow-1">
                          <input
                            type="text"
                            className={`form-control form-control-solid form-control-lg ${
                              formik.touched.mobileNumber &&
                              formik.errors.mobileNumber
                            }`}
                            placeholder="Mobile Number"
                            {...formik.getFieldProps("mobileNumber")}
                          />
                          {formik.touched.mobileNumber &&
                            formik.errors.mobileNumber && (
                              <div className="fv-plugins-message-container">
                                <div className="fv-help-block text-danger">
                                  {formik.errors.mobileNumber}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      type="email"
                      value={formik.getFieldProps("email")}
                      title="Email ID"
                      touchedName={formik.touched.email}
                      errorsName={formik.errors.email}
                    />
                  </div>
                </div>

                {/* Photo Upload Section */}
                <div className="row mb-6">
                  {/* Photo Upload Column */}
                  <div className="col-xl-4 col-lg-5 col-md-12 mb-6 mb-lg-0">
                    <div className="form-group">
                      <label className="form-label required">
                        Take A Photo of Your Face
                      </label>
                      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-4">
                        <div
                          className="image-input image-input-outline flex-shrink-0"
                          data-kt-image-input="true"
                        >
                          <div
                            className="image-input-wrapper position-relative"
                            onClick={openGallery}
                            style={{
                              backgroundImage: imagePreview
                                ? `url(${imagePreview})`
                                : "none",
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                              width: "160px",
                              height: "160px",
                              border: imagePreview
                                ? "3px solid #e4e6ef"
                                : "2px dashed #6c757d",
                              borderRadius: "50%",
                              backgroundColor: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: imagePreview
                                ? "0 4px 12px rgba(0,0,0,0.1)"
                                : "0 2px 8px rgba(0,0,0,0.04)",
                              transition: "all 0.3s ease",
                              overflow: "visible", // Changed from "hidden" to make button visible
                              cursor: "pointer",
                            }}
                            title="Click to select photo"
                          >
                            {/* Remove Image Button */}
                            {imagePreview && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent triggering the circle's onClick
                                  setImagePreview(null);
                                  formik.setFieldValue("photo", null);
                                  if (fileInputRef.current)
                                    fileInputRef.current.value = "";
                                }}
                                style={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  background: "#fff",
                                  border: "2px solid #d33",
                                  borderRadius: "50%",
                                  width: 32,
                                  height: 32,
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  zIndex: 10,
                                  boxShadow: "0 2px 8px rgba(211, 51, 51, 0.3)",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#d33";
                                  const icon = e.currentTarget.querySelector(
                                    "i"
                                  ) as HTMLElement;
                                  if (icon) icon.style.color = "#fff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  const icon = e.currentTarget.querySelector(
                                    "i"
                                  ) as HTMLElement;
                                  if (icon) icon.style.color = "#d33";
                                }}
                                title="Remove Image"
                              >
                                <i
                                  className="bi bi-trash-fill"
                                  style={{
                                    color: "#d33",
                                    fontSize: "0.875rem",
                                    pointerEvents: "none",
                                  }}
                                />
                              </button>
                            )}
                            {/* Camera icon and text if no image */}
                            {!imagePreview && (
                              <div
                                style={{
                                  color: "#6c757d",
                                  fontSize: "2.5rem",
                                  opacity: 0.4,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <i className="bi bi-camera" />
                                <span
                                  style={{
                                    textAlign: "center",
                                    fontSize: "0.95rem",
                                    marginTop: 8,
                                  }}
                                >
                                  Self-Portrait <br />
                                  Photo
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <button
                            type="button"
                            className="btn btn-light-primary btn-sm"
                            onClick={openCamera}
                          >
                            <i className="bi bi-camera me-2"></i>
                            Take A Photo
                          </button>
                          <button
                            type="button"
                            className="btn btn-light-info btn-sm"
                            onClick={openGallery}
                          >
                            <i className="bi bi-image me-2"></i>
                            Choose from Gallery
                          </button>
                          {imagePreview && (originalPhotoData || imagePreview) && (
                            <button
                              type="button"
                              className="btn btn-light-warning btn-sm"
                              onClick={handleReCrop}
                            >
                              <i className="bi bi-crop me-2"></i>
                              Re-crop Photo
                            </button>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="d-none"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handlePhotoUpload}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Photo Guidelines Column with Example */}
                  <div className="col-xl-8 col-lg-7 col-md-12">
                    <div className="alert alert-primary h-100 px-4 py-4">
                      <div className="row align-items-center">
                        <div className="col-md-8 col-sm-12">
                          <div style={{ fontSize: "0.9rem" }}>
                            <strong>Photo Guidelines:</strong>
                            <ul className="mb-0 ps-4 mt-2">
                              <li>
                                Take or Upload a recent self-portrait
                                photograph.
                              </li>
                              <li>
                                Face the camera directly with your full face
                                visible.
                              </li>
                              <li>
                                Do <b>not</b> crop your head or cut off any part
                                of your face.
                              </li>
                              <li>
                                Do <b>not</b> use group photos or photos with
                                other people.
                              </li>
                              <li>
                                Use a plain, light background if possible.
                              </li>
                              <li>
                                Avoid hats, sunglasses, or anything that covers
                                your face.
                              </li>
                              <li>
                                <strong>File format: JPG or PNG only</strong>{" "}
                                (WebP & HEIC not supported).
                              </li>
                              <li>Maximum file size: 15MB.</li>
                            </ul>
                          </div>
                        </div>
                        <div className="col-md-4 col-sm-12 text-center mt-6">
                          <div className="row">
                            {/* Good Example */}
                            <div className="col-xl-6 col-md-12 col-6">
                              <div className="text-center mb-3">
                                <div className="mb-2">
                                  <img
                                    src="/media/logos/placeholder.png"
                                    alt="Proper self-portrait example"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      border: "2px solid #ffffff",
                                      borderRadius: "50%",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    }}
                                  />
                                </div>
                                <small className="text-white fw-bold bg-success px-2 py-1 rounded d-block">
                                  ✓ Good
                                </small>
                              </div>
                            </div>
                            {/* Bad Example */}
                            <div className="col-xl-6 col-md-12 col-6">
                              <div className="text-center mb-3">
                                <div className="mb-2">
                                  <img
                                    src="/media/logos/placeholder-bad.png"
                                    alt="Improperly cropped example"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      border: "2px solid #ffffff",
                                      borderRadius: "50%",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    }}
                                  />
                                </div>
                                <small className="text-white fw-bold bg-danger px-2 py-1 rounded d-block">
                                  ✗ Bad
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {formik.touched.photo && formik.errors.photo && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block text-danger">
                        {formik.errors.photo}
                      </div>
                    </div>
                  )}
                </div>

                {/* Mandir/Mandal Information Section */}
                <div className="section-header mb-6 mt-8">
                  <h3 className="section-title">
                    <i className="fas fa-hand-holding-heart me-2"></i>
                    Mandir/Mandal Information
                  </h3>
                </div>

                <div className="row mb-8">
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-4 mb-md-0">
                    <InputSelectWrapper
                      value={formik.getFieldProps("localMandir")}
                      title="Local Mandir/Mandal"
                      touchedName={formik.touched.localMandir}
                      errorsName={formik.errors.localMandir}
                      DataArr={mandirOptions}
                      Cid={id}
                      handleChange={handleLocalMandirChange}
                      isRoot="-1"
                      size="12"
                      required="1"
                      loading={loadingMandirs} // Pass loading state
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 mb-4 mb-md-0">
                    <InputSelectWrapper
                      value={formik.getFieldProps("nativeVillage")}
                      title="Native Village"
                      touchedName={formik.touched.nativeVillage}
                      errorsName={formik.errors.nativeVillage}
                      DataArr={nativeVillageOptions}
                      Cid={id}
                      handleChange={handleNativeVillageChange}
                      isRoot="-1"
                      size="12"
                      required="1"
                      placeholder="Village or Other"
                    />

                    {showCustomNativeVillage && (
                      <div className="mt-3">
                        <InputTextWrapper
                          required="1"
                          size="12"
                          value={{
                            value: formik.values.customNativeVillage || "",
                            onChange: handleCustomNativeVillageChange,
                          }}
                          title="Specify Native Village"
                          touchedName={formik.touched.customNativeVillage}
                          errorsName={formik.errors.customNativeVillage}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="section-header mb-6 mt-8">
                  <h3 className="section-title">
                    <i className="fas fa-map-marker-alt me-2"></i>Address
                    Information
                  </h3>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <InputSelectWrapper
                      value={formik.getFieldProps("country")}
                      title="Country"
                      touchedName={formik.touched.country}
                      errorsName={formik.errors.country}
                      DataArr={countryOptions}
                      Cid={id}
                      handleChange={handleCountryChange}
                      isRoot="-1"
                      size="12"
                    />
                  </div>
                </div>
                <div className="row mb-6">
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-3">
                    <label className="col-form-label required fw-bold fs-6">
                      Home Address
                    </label>
                    <GooglePlacesAutocomplete
                      value={formik.values.street}
                      onChange={(val) => formik.setFieldValue("street", val)}
                      onAddressSelect={(address) => {
                        formik.setFieldValue("street", address.street);
                        formik.setFieldValue("area", address.area);
                        formik.setFieldValue("city", address.city);
                        formik.setFieldValue("state", address.state);
                        formik.setFieldValue("postcode", address.postcode);
                        formik.setFieldValue("country", address.country);
                      }}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-3">
                    <InputTextWrapper
                      required={formik.values.country === "India" ? "1" : "0"}
                      size="12"
                      value={formik.getFieldProps("area")}
                      placeholder="e.g. Nikol or Vastral"
                      title="Area"
                      touchedName={formik.touched.area}
                      errorsName={formik.errors.area}
                    />
                  </div>
                </div>
                <div className="col-lg-12 col-md-12 col-sm-12 mb-3 d-none invisible">
                  <InputTextWrapper
                    required="1"
                    size="12"
                    value={formik.getFieldProps("street")}
                    title="House No. & Street Name"
                    touchedName={formik.touched.street}
                    errorsName={formik.errors.street}
                  />
                </div>

                <div className="row">
                  <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={formik.getFieldProps("city")}
                      title="City"
                      touchedName={formik.touched.city}
                      errorsName={formik.errors.city}
                    />
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
                    <InputTextWrapper
                      required="0"
                      size="12"
                      value={formik.getFieldProps("state")}
                      title="State/Province"
                      touchedName={formik.touched.state}
                      errorsName={formik.errors.state}
                    />
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={formik.getFieldProps("postcode")}
                      title="Postal/PIN/ZIP Code"
                      touchedName={formik.touched.postcode}
                      errorsName={formik.errors.postcode}
                    />
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="section-header mb-6 mt-8">
                  <h3 className="section-title">
                    <i className="fas fa-user me-2"></i>Personal Information
                  </h3>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-12 col-md-12 col-sm-12 mb-6">
                    <label className="col-form-label required">
                      Date of Birth
                    </label>
                    <InputDateNode
                      value={{
                        ...formik.getFieldProps("dateOfBirth"),
                        value: formik.values.dateOfBirth || "",
                      }}
                      title="Date of Birth"
                      touchedName={formik.touched.dateOfBirth}
                      errorsName={formik.errors.dateOfBirth}
                      handleChange={handleDateChange}
                      maxDate={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-6">
                    <div className="form-group">
                      <label className="form-label required">Gender</label>
                      <div className="d-flex flex-wrap gap-3">
                        {genderOptions.map((option) => (
                          <InputRadioWrapper
                            key={option._id}
                            name="gender"
                            value={option._id}
                            label={option.name}
                            checked={formik.values.gender === option._id}
                            onChange={() => handleGender(option._id)}
                          />
                        ))}
                      </div>
                      {formik.touched.gender && formik.errors.gender && (
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block text-danger">
                            {formik.errors.gender}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 mb-6">
                    <div className="form-group">
                      <label className="form-label required">
                        Marital Status
                      </label>
                      <div className="d-flex flex-wrap gap-3">
                        {maritalStatusOptions.map((option) => (
                          <InputRadioWrapper
                            key={option._id}
                            name="maritalStatus"
                            value={option._id}
                            label={option.name}
                            checked={formik.values.maritalStatus === option._id}
                            onChange={() => handleMaritalStatus(option._id)}
                          />
                        ))}
                      </div>
                      {formik.touched.maritalStatus &&
                        formik.errors.maritalStatus && (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block text-danger">
                              {formik.errors.maritalStatus}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <div className="row mb-6">
                  <div className="col-sm-12">
                    <div className="form-group">
                      <label className="form-label required mb-3">
                        Blood Group
                      </label>
                      <div className="d-flex flex-wrap gap-3">
                        {bloodGroupOptions.map((option) => (
                          <InputRadioWrapper
                            key={option._id}
                            name="bloodGroup"
                            value={option._id}
                            label={option.name}
                            checked={formik.values.bloodGroup === option._id}
                            onChange={() => handleBloodGroupChange(option._id)}
                          />
                        ))}
                      </div>
                      {formik.touched.bloodGroup && formik.errors.bloodGroup && (
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block text-danger">
                            {formik.errors.bloodGroup}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Professional Information Section */}
                <div className="section-header mb-6 mt-12">
                  <h3 className="section-title">
                    <i className="fas fa-briefcase me-2"></i>Professional
                    Information
                  </h3>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-4 mb-lg-0">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={formik.getFieldProps("education")}
                      title="Education"
                      placeholder="High School, College, Apprenticeship, BSC, MSc, PhD"
                      touchedName={formik.touched.education}
                      errorsName={formik.errors.education}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 mb-4 mb-lg-0">
                    <InputTextWrapper
                      required="1"
                      size="12"
                      value={formik.getFieldProps("profession")}
                      title="Profession"
                      placeholder="Engineer, Teacher, Doctor, Farmer, Businessman"
                      touchedName={formik.touched.profession}
                      errorsName={formik.errors.profession}
                    />
                  </div>
                </div>

                <div className="row mb-8">
                  <div className="col-lg-6 col-md-6 col-sm-12">
                    <InputSwitch
                      required="0"
                      name="isRetired"
                      checked={formik.values.isRetired}
                      onChange={handleRetiredChange}
                      title="Are you retired?"
                    />
                  </div>
                </div>

                {/* Seva Preferences Section */}
                <div className="section-header mb-6 mt-8">
                  <h3 className="section-title">
                    <i className="fas fa-hands-helping me-2"></i>Seva
                    Preferences
                  </h3>
                </div>

                <div className="row mb-6">
                  <div className="col-lg-12">
                    <InputSwitch
                      required="0"
                      name="wouldLikeToVolunteer"
                      checked={formik.values.wouldLikeToVolunteer}
                      onChange={handlewouldLikeToVolunteerChange}
                      title="Would you like to volunteer for Seva?"
                    />
                  </div>
                </div>

                {formik.values.wouldLikeToVolunteer && (
                  <div className="row mb-6">
                    <div className="col-lg-12">
                      <div className="form-group">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <label className="form-label required mb-0">
                            Seva Departments
                          </label>
                          <button
                            type="button"
                            className="btn btn-sm btn-light-primary"
                            onClick={() => setShowCustomSevaDepartment(true)}
                          >
                            <i className="bi bi-plus-lg me-2"></i>
                            Add Custom Department
                          </button>
                        </div>

                        <div className="d-flex flex-wrap">
                          {sevaOptions.map((option) => (
                            <div
                              key={option.value}
                              className="form-check me-10 mb-3"
                            >
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`seva-${option.value}`}
                                value={option.value}
                                checked={
                                  formik.values.sevaDepartments?.includes(
                                    option.value
                                  ) || false
                                }
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const currentValues =
                                    formik.values.sevaDepartments || [];

                                  if (isChecked) {
                                    formik.setFieldValue("sevaDepartments", [
                                      ...currentValues,
                                      option.value,
                                    ]);
                                  } else {
                                    formik.setFieldValue(
                                      "sevaDepartments",
                                      currentValues.filter(
                                        (v) => v !== option.value
                                      )
                                    );
                                  }
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`seva-${option.value}`}
                              >
                                {option.label}
                              </label>
                            </div>
                          ))}
                        </div>

                        {/* Custom Seva Department Input */}
                        {showCustomSevaDepartment && (
                          <div className="row mt-4">
                            <div className="col-lg-6 col-md-8 col-sm-12">
                              <div className="card card-flush py-4">
                                <div className="card-body pt-0">
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Department Name
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control form-control-solid"
                                      placeholder="Enter custom seva department name"
                                      value={customSevaDepartment}
                                      onChange={
                                        handleCustomSevaDepartmentChange
                                      }
                                      onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          addCustomSevaDepartment();
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-light"
                                      onClick={() => {
                                        setShowCustomSevaDepartment(false);
                                        setCustomSevaDepartment("");
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={addCustomSevaDepartment}
                                      disabled={!customSevaDepartment.trim()}
                                    >
                                      <i className="bi bi-plus-lg me-2"></i>
                                      Add Department
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Display selected seva departments */}
                        {formik.values.sevaDepartments &&
                          formik.values.sevaDepartments.length > 0 && (
                            <div className="mt-4">
                              <h6 className="text-muted mb-2">
                                Selected Departments:
                              </h6>
                              <div className="d-flex flex-wrap gap-2">
                                {formik.values.sevaDepartments.map((dept) => (
                                  <span
                                    key={dept}
                                    className="badge badge-light-primary d-flex align-items-center"
                                  >
                                    {dept}
                                    <button
                                      type="button"
                                      className="btn btn-icon btn-sm btn-text-danger ms-2"
                                      onClick={() => {
                                        const updated =
                                          formik.values.sevaDepartments.filter(
                                            (d) => d !== dept
                                          );
                                        formik.setFieldValue(
                                          "sevaDepartments",
                                          updated
                                        );
                                      }}
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                        {formik.touched.sevaDepartments &&
                          formik.errors.sevaDepartments && (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block text-danger">
                                {formik.errors.sevaDepartments}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="alert align-items-center p-5 border border-2">
                  {/* Data Processing & Consent Section (GDPR) - Only shown for mandals requiring GDPR consent */}
                  {consentConfig.requiresGdprConsent && (
                    <div className="mt-4 mb-4 p-4 rounded border border-primary border-3 bg-white shadow">
                      <div className="alert alert-primary mb-4">
                        <i className="fas fa-shield-alt me-2"></i>
                        <strong>
                          Privacy Notice{" "}
                          {formik.values.localMandir &&
                          mandirOptions.find(
                            (m) => m.value === formik.values.localMandir
                          )?.label
                            ? `for ${
                                mandirOptions.find(
                                  (m) => m.value === formik.values.localMandir
                                )?.label
                              }`
                            : ""}
                        </strong>
                      </div>

                      {/* Dynamic GDPR Intro Text */}
                      {consentConfig.gdprIntroText && (
                        <p className="fs-6">{consentConfig.gdprIntroText}</p>
                      )}

                      {/* Dynamic GDPR Data Usage Text */}
                      {consentConfig.gdprDataUsageText && (
                        <p className="fs-6">
                          {consentConfig.gdprDataUsageText}
                        </p>
                      )}

                      {/* Dynamic GDPR Legal Basis Text */}
                      {consentConfig.gdprLegalBasisText && (
                        <p className="fs-6">
                          {consentConfig.gdprLegalBasisText}
                        </p>
                      )}

                      {/* Dynamic GDPR Data Sharing Text */}
                      {consentConfig.gdprDataSharingText && (
                        <p className="fs-6">
                          {consentConfig.gdprDataSharingText}
                        </p>
                      )}

                      {/* Dynamic GDPR Data Storage Text */}
                      {consentConfig.gdprDataStorageText && (
                        <p className="fs-6">
                          {consentConfig.gdprDataStorageText}
                        </p>
                      )}

                      {/* Privacy Policy Link */}
                      {consentConfig.privacyPolicyUrl && (
                        <p className="fs-6">
                          For more information, please read the privacy policy
                          at:{" "}
                          <a
                            href={consentConfig.privacyPolicyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {consentConfig.privacyPolicyUrl}
                          </a>
                        </p>
                      )}

                      <div className="mt-3">
                        {consentConfig.requiresBloodTypeConsent && (
                          <>
                            <h6 className="fw-semibold">
                              {consentConfig.gdprSpecialCategoryTitle ||
                                "Special category data"}{" "}
                              <span className="text-danger">*</span>
                            </h6>
                            <p className="fs-6 mb-2">
                              {consentConfig.gdprSpecialCategoryText ||
                                "In situations that enable you to give your explicit consent under Article 9(2)(a) we will process your health data if you choose to provide it specifically your blood type under this legal basis."}
                            </p>

                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="consentBloodType"
                                checked={formik.values.consentBloodType}
                                required
                                onChange={async (e) => {
                                  const isChecked = e.target.checked;
                                  await formik.setFieldValue(
                                    "consentBloodType",
                                    isChecked,
                                    true // validate immediately
                                  );
                                  await formik.setFieldTouched(
                                    "consentBloodType",
                                    true,
                                    false
                                  );

                                  if (isChecked) {
                                    // Auto-populate name and date
                                    const fullName =
                                      `${formik.values.firstName} ${formik.values.middleName} ${formik.values.lastName}`.trim();
                                    const todayDate = new Date()
                                      .toISOString()
                                      .split("T")[0];
                                    formik.setFieldValue(
                                      "consentBloodTypeName",
                                      fullName
                                    );
                                    formik.setFieldValue(
                                      "consentBloodTypeDate",
                                      todayDate
                                    );
                                  }
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="consentBloodType"
                              >
                                {consentConfig.gdprSpecialCategoryConsentLabel ||
                                  "I give my explicit consent for Maninagar Shree Swaminarayan Gadi Sansthan and my local Mandal to process my blood type for health treatment purposes."}{" "}
                                <span className="text-danger">*</span>
                              </label>
                            </div>

                            {formik.touched.consentBloodType &&
                              formik.errors.consentBloodType && (
                                <div className="fv-plugins-message-container">
                                  <div className="fv-help-block text-danger">
                                    {formik.errors.consentBloodType}
                                  </div>
                                </div>
                              )}

                            {formik.values.consentBloodType && (
                              <>
                                <div className="row mb-3 mt-3">
                                  <div className="col-lg-6 col-md-6 col-sm-12">
                                    <p className="text-muted mb-1 small">
                                      Name
                                    </p>
                                    <p className="fw-semibold">
                                      {formik.values.consentBloodTypeName ||
                                        "-"}
                                    </p>
                                  </div>
                                  <div className="col-lg-6 col-md-6 col-sm-12">
                                    <p className="text-muted mb-1 small">
                                      Date
                                    </p>
                                    <p className="fw-semibold">
                                      {formik.values.consentBloodTypeDate
                                        ? formatDateLong(
                                            formik.values.consentBloodTypeDate
                                          )
                                        : "-"}
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}

                            <hr />

                            <h6 className="fw-semibold">
                              {consentConfig.gdprRestrictedTransferTitle ||
                                "Explicit Consent to 'Restricted Transfer' to India"}{" "}
                              <span className="text-danger">*</span>
                            </h6>
                            <p className="fs-6 mb-2">
                              {consentConfig.gdprRestrictedTransferText ||
                                "If you choose to transfer your data to India, Maninagar Shree Swaminarayan Gadi Sansthan and your local Mandal, will use it to enhance security, access control, streamlining event management and similar activities. Health data will be used to treat your health. All data on this registration form will be shared with Maninagar, India. Please be aware that transferring your data to India may pose certain risks as it may not have the same level of data protection and safeguards as provided under UK GDPR and UK data protection laws."}
                            </p>

                            <div className="form-check mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="consentRestrictedTransfer"
                                checked={
                                  formik.values.consentRestrictedTransfer
                                }
                                required
                                onChange={async (e) => {
                                  const isChecked = e.target.checked;
                                  await formik.setFieldValue(
                                    "consentRestrictedTransfer",
                                    isChecked,
                                    true // validate immediately
                                  );
                                  await formik.setFieldTouched(
                                    "consentRestrictedTransfer",
                                    true,
                                    false
                                  );

                                  if (isChecked) {
                                    // Auto-populate name and date
                                    const fullName =
                                      `${formik.values.firstName} ${formik.values.middleName} ${formik.values.lastName}`.trim();
                                    const todayDate = new Date()
                                      .toISOString()
                                      .split("T")[0];
                                    formik.setFieldValue(
                                      "consentRestrictedTransferName",
                                      fullName
                                    );
                                    formik.setFieldValue(
                                      "consentRestrictedTransferDate",
                                      todayDate
                                    );
                                  }
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="consentRestrictedTransfer"
                              >
                                {consentConfig.gdprRestrictedTransferConsentLabel ||
                                  "I understand the above and give my explicit consent to the restricted transfer to India."}{" "}
                                <span className="text-danger">*</span>
                              </label>
                            </div>

                            {formik.touched.consentRestrictedTransfer &&
                              formik.errors.consentRestrictedTransfer && (
                                <div className="fv-plugins-message-container">
                                  <div className="fv-help-block text-danger">
                                    {formik.errors.consentRestrictedTransfer}
                                  </div>
                                </div>
                              )}

                            {formik.values.consentRestrictedTransfer && (
                              <>
                                <div className="row mb-3 mt-3">
                                  <div className="col-lg-6 col-md-6 col-sm-12">
                                    <p className="text-muted mb-1 small">
                                      Name
                                    </p>
                                    <p className="fw-semibold">
                                      {formik.values
                                        .consentRestrictedTransferName || "-"}
                                    </p>
                                  </div>
                                  <div className="col-lg-6 col-md-6 col-sm-12">
                                    <p className="text-muted mb-1 small">
                                      Date
                                    </p>
                                    <p className="fw-semibold">
                                      {formik.values
                                        .consentRestrictedTransferDate
                                        ? formatDateLong(
                                            formik.values
                                              .consentRestrictedTransferDate
                                          )
                                        : "-"}
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}

                            <p className="fs-6 mb-2">
                              {consentConfig.gdprWithdrawalText || (
                                <>
                                  Once you have submitted the form the
                                  restricted transfer is immediate. If you wish
                                  to withdraw consent please contact{" "}
                                  <a href="mailto:Maninagar@SwaminarayanGadi.com">
                                    Maninagar@SwaminarayanGadi.com
                                  </a>{" "}
                                  after which your data will be deleted within a
                                  calendar month.
                                </>
                              )}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Hardcoded Disclaimer - Only shown when GDPR consent is NOT required */}
                  {!consentConfig.requiresGdprConsent && (
                    <>
                      <p className="fs-3 fw-bold mb-4">
                        Read this privacy notice before submitting the form
                      </p>
                      <p className="fs-4">
                        We will be collecting your personal details, and, if you
                        wish, your family members’ details, to keep our member
                        and community records up to date. This information will
                        only be used for official organisational purposes, such
                        as communication, coordination of events, record
                        keeping, and, if necessary, in the case of emergencies.
                        In line with the UK General Data Protection Regulation
                        (UK GDPR), your data will be stored securely in the UK
                        and will not be shared with anyone outside the
                        organisation without your consent, unless required by
                        law. You are entitled to request a copy of your data,
                        correct it, or ask for it to be removed at any time. By
                        providing your details today, you are giving us your
                        consent to use and store this information as explained.
                      </p>

                      {/* Disclaimer Checkbox  */}
                      <div className="form-check me-10 mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="acceptDisclaimer"
                          {...formik.getFieldProps("acceptDisclaimer")}
                          checked={formik.values.acceptDisclaimer}
                        />
                        <label
                          className="form-check-label fs-4"
                          htmlFor="acceptDisclaimer"
                        >
                          I accept that I have read and agree to the above
                          privacy notice.
                        </label>
                      </div>
                      {formik.touched.acceptDisclaimer &&
                        formik.errors.acceptDisclaimer && (
                          <div className="fv-plugins-message-container">
                            <div className="fv-help-block text-white bg-danger p-2 mt-2 rounded">
                              {formik.errors.acceptDisclaimer}
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
                {Object.keys(formik.errors).length > 0 && (
                  <div className="alert alert-danger mt-4">
                    <strong>Please fix the following errors:</strong>
                    <ul className="mb-0">
                      {Object.entries(formik.errors).map(([field, error]) => (
                        <li key={field}>{error as string}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end pb-9 px-9">
                <button
                  type="button"
                  className="btn btn-primary btn-lg w-100"
                  onClick={handlePreview}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      {id ? "Update" : "Review Your Details"}
                      {!id && <i className="fas fa-arrow-right ms-2"></i>}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-crop me-2"></i>Crop Your Photo
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCropModal(false);
                    setImageToCrop(null);
                    setCropError(null);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Crop your photo to fit the circle:</strong> Use the
                  slider/buttons to zoom in/out and drag the image to position
                  it perfectly. On mobile, you can pinch to zoom.
                </div>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "400px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px",
                    overflow: "hidden",
                    touchAction: "none", // Prevent default touch behaviors including pinch-zoom
                  }}
                >
                  {/* Crop Container */}
                  <div
                    ref={cropContainerRef}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      touchAction: "none", // Prevent default touch behaviors
                    }}
                    onMouseDown={(e: React.MouseEvent) => {
                      // Desktop dragging (mouse)
                      setCropError(null);
                      if ((e as any).button !== 0) return; // only left click
                      const startX = e.clientX - crop.x;
                      const startY = e.clientY - crop.y;

                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        setCrop({
                          x: moveEvent.clientX - startX,
                          y: moveEvent.clientY - startY,
                        });
                      };

                      const handleMouseUp = () => {
                        document.removeEventListener(
                          "mousemove",
                          handleMouseMove
                        );
                        document.removeEventListener("mouseup", handleMouseUp);
                      };

                      document.addEventListener("mousemove", handleMouseMove);
                      document.addEventListener("mouseup", handleMouseUp);
                    }}
                    onTouchStart={(e) => {
                      setCropError(null);

                      // Detect pinch gesture (two fingers)
                      if (e.touches.length === 2) {
                        e.preventDefault();
                        isPinching.current = true;

                        const touch1 = e.touches[0];
                        const touch2 = e.touches[1];
                        const distance = Math.hypot(
                          touch2.clientX - touch1.clientX,
                          touch2.clientY - touch1.clientY
                        );

                        pinchStartDistance.current = distance;
                        pinchStartZoom.current = zoom;

                        console.log("Pinch started:", { distance, zoom });
                      }
                      // Single touch for dragging
                      else if (e.touches.length === 1) {
                        isPinching.current = false;
                        const touch = e.touches[0];
                        const startX = touch.clientX - crop.x;
                        const startY = touch.clientY - crop.y;

                        const handleTouchMove = (moveEvent: TouchEvent) => {
                          moveEvent.preventDefault();

                          // Switch to pinch mode if second finger added
                          if (moveEvent.touches.length === 2) {
                            if (!isPinching.current) {
                              isPinching.current = true;
                              const touch1 = moveEvent.touches[0];
                              const touch2 = moveEvent.touches[1];
                              pinchStartDistance.current = Math.hypot(
                                touch2.clientX - touch1.clientX,
                                touch2.clientY - touch1.clientY
                              );
                              pinchStartZoom.current = zoom;
                            }

                            const touch1 = moveEvent.touches[0];
                            const touch2 = moveEvent.touches[1];
                            const currentDistance = Math.hypot(
                              touch2.clientX - touch1.clientX,
                              touch2.clientY - touch1.clientY
                            );

                            if (
                              pinchStartDistance.current &&
                              pinchStartDistance.current > 0
                            ) {
                              const scale =
                                currentDistance / pinchStartDistance.current;
                              const newZoom = Math.min(
                                3,
                                Math.max(0.01, pinchStartZoom.current * scale)
                              );
                              setZoom(newZoom);
                              console.log("Pinching:", {
                                currentDistance,
                                scale,
                                newZoom,
                              });
                            }
                            return;
                          }

                          // Continue dragging if still single touch
                          if (
                            moveEvent.touches.length === 1 &&
                            !isPinching.current
                          ) {
                            const moveTouch = moveEvent.touches[0];
                            setCrop({
                              x: moveTouch.clientX - startX,
                              y: moveTouch.clientY - startY,
                            });
                          }
                        };

                        const handleTouchEnd = (endEvent: TouchEvent) => {
                          // If still have touches, might be continuing with one finger
                          if (endEvent.touches.length === 0) {
                            isPinching.current = false;
                            pinchStartDistance.current = null;
                            document.removeEventListener(
                              "touchmove",
                              handleTouchMove
                            );
                            document.removeEventListener(
                              "touchend",
                              handleTouchEnd
                            );
                            console.log("Touch ended");
                          } else if (endEvent.touches.length === 1) {
                            // Went from 2 fingers to 1, reset for dragging
                            isPinching.current = false;
                            pinchStartDistance.current = null;
                          }
                        };

                        document.addEventListener(
                          "touchmove",
                          handleTouchMove,
                          { passive: false }
                        );
                        document.addEventListener("touchend", handleTouchEnd);
                      }
                    }}
                  >
                    <img
                      ref={cropImageRef}
                      src={imageToCrop}
                      alt="Crop preview"
                      style={{
                        // Scale based on container and zoom - use max-width with zoom multiplier
                        maxWidth: "none",
                        maxHeight: "none",
                        width: "auto",
                        height: "auto",
                        transform: `translate(${crop.x}px, ${crop.y}px) scale(${zoom})`,
                        transformOrigin: "center center",
                        cursor: "move",
                        userSelect: "none",
                        // For touch devices, let container handle pointer events; for mouse allow events
                        pointerEvents: isTouchDevice ? "none" : "auto",
                      }}
                      draggable="false"
                      onLoad={() => {
                        console.log(
                          "Crop image loaded successfully:",
                          imageToCrop
                        );
                      }}
                      onError={(e) => {
                        console.error(
                          "Failed to load crop image:",
                          imageToCrop
                        );
                        console.error("Image error event:", e);
                        setCropError(
                          "Failed to load image. Please try uploading a new photo."
                        );
                      }}
                      onMouseDown={(e) => {
                        setCropError(null); // Clear error when user starts dragging
                        const startX = e.clientX - crop.x;
                        const startY = e.clientY - crop.y;

                        const handleMouseMove = (moveEvent: MouseEvent) => {
                          setCrop({
                            x: moveEvent.clientX - startX,
                            y: moveEvent.clientY - startY,
                          });
                        };

                        const handleMouseUp = () => {
                          document.removeEventListener(
                            "mousemove",
                            handleMouseMove
                          );
                          document.removeEventListener(
                            "mouseup",
                            handleMouseUp
                          );
                        };

                        document.addEventListener("mousemove", handleMouseMove);
                        document.addEventListener("mouseup", handleMouseUp);
                      }}
                    />

                    {/* Circular Guideline Overlay */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: `${cropSize}px`,
                        height: `${cropSize}px`,
                        border: `3px solid ${
                          cropError ? "#dc3545" : "#28a745"
                        }`,
                        borderRadius: "50%",
                        pointerEvents: "none",
                        boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                        overflow: "hidden",
                      }}
                    >
                      {/* Face and Shoulders Guide Overlay */}
                      <img
                        src="/media/icons/face-guide.svg"
                        alt="Face guide"
                        style={{
                          position: "absolute",
                          top: "5%",
                          left: "0",
                          width: "100%",
                          height: "100%",
                          pointerEvents: "none",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {cropError && (
                  <div className="alert alert-warning mt-4 mb-0" role="alert">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle-fill me-2 fs-4"></i>
                      <div>
                        <strong>Position Issue:</strong>
                        <p className="mb-0 mt-1">{cropError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Zoom Control */}
                <div className="mt-5 pt-3">
                  <label className="form-label d-flex align-items-center justify-content-between mb-4">
                    <span className="fs-5">
                      <i className="bi bi-zoom-in me-2"></i>Zoom
                    </span>
                    <span className="badge badge-light-primary fs-6 px-3 py-2">
                      {Math.round(zoom * 100)}%
                    </span>
                  </label>

                  {/* Zoom Buttons and Slider */}
                  <div className="d-flex align-items-center gap-3">
                    {/* Zoom Out Button */}
                    <button
                      type="button"
                      className="btn btn-icon btn-light-primary"
                      onClick={() => {
                        const newZoom = Math.max(0.01, zoom - 0.1);
                        setZoom(newZoom);
                        setCropError(null);
                      }}
                      disabled={zoom <= 0.01}
                      style={{
                        width: "40px",
                        height: "40px",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title="Zoom Out"
                    >
                      <i className="bi bi-dash-lg fs-4"></i>
                    </button>

                    {/* Slider Container */}
                    <div style={{ flex: 1 }}>
                      <style>{`
                    /* Custom styling for larger, easier-to-use slider thumb */
                    .zoom-slider::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      appearance: none;
                      width: 28px;
                      height: 28px;
                      border-radius: 50%;
                      background: #3699FF;
                      cursor: pointer;
                      border: 3px solid #fff;
                      box-shadow: 0 2px 8px rgba(54, 153, 255, 0.4);
                      transition: all 0.2s ease;
                      margin-top: -10px; /* Centers the thumb on the track */
                    }
                    
                    .zoom-slider::-webkit-slider-thumb:hover {
                      background: #2684E8;
                      transform: scale(1.1);
                      box-shadow: 0 3px 12px rgba(54, 153, 255, 0.6);
                    }
                    
                    .zoom-slider::-webkit-slider-thumb:active {
                      transform: scale(1.15);
                      box-shadow: 0 4px 16px rgba(54, 153, 255, 0.8);
                    }
                    
                    .zoom-slider::-moz-range-thumb {
                      width: 28px;
                      height: 28px;
                      border-radius: 50%;
                      background: #3699FF;
                      cursor: pointer;
                      border: 3px solid #fff;
                      box-shadow: 0 2px 8px rgba(54, 153, 255, 0.4);
                      transition: all 0.2s ease;
                    }
                    
                    .zoom-slider::-moz-range-thumb:hover {
                      background: #2684E8;
                      transform: scale(1.1);
                      box-shadow: 0 3px 12px rgba(54, 153, 255, 0.6);
                    }
                    
                    .zoom-slider::-moz-range-thumb:active {
                      transform: scale(1.15);
                      box-shadow: 0 4px 16px rgba(54, 153, 255, 0.8);
                    }
                    
                    .zoom-slider::-webkit-slider-runnable-track {
                      height: 8px;
                      border-radius: 4px;
                      background: #e4e6ef;
                    }
                    
                    .zoom-slider::-moz-range-track {
                      height: 8px;
                      border-radius: 4px;
                      background: #e4e6ef;
                    }
                  `}</style>
                      <input
                        type="range"
                        className="form-range zoom-slider"
                        min="0.01"
                        max="3"
                        step="0.01"
                        value={zoom}
                        onChange={(e) => {
                          setZoom(parseFloat(e.target.value));
                          setCropError(null); // Clear error when user adjusts zoom
                        }}
                        style={{
                          height: "12px",
                          cursor: "pointer",
                        }}
                      />
                      <div className="d-flex justify-content-between text-muted mt-2">
                        <span className="fs-6">1%</span>
                        <span className="fs-6">300%</span>
                      </div>
                    </div>

                    {/* Zoom In Button */}
                    <button
                      type="button"
                      className="btn btn-icon btn-light-primary"
                      onClick={() => {
                        const newZoom = Math.min(3, zoom + 0.1);
                        setZoom(newZoom);
                        setCropError(null);
                      }}
                      disabled={zoom >= 3}
                      style={{
                        width: "40px",
                        height: "40px",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title="Zoom In"
                    >
                      <i className="bi bi-plus-lg fs-4"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setShowCropModal(false);
                    setImageToCrop(null);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setCropError(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCropSave}
                >
                  <i className="bi bi-check-circle me-2"></i>Apply Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { CURD };
